import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
} from '@mui/material';
import MuiLink from '@mui/material/Link';
import { keyframes } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import { supabaseAuthService } from '../services/supabaseAuthService';
import toast from 'react-hot-toast';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      // Use Supabase's resetPasswordForEmail which will send the email
      const { error: resetError } = await supabaseAuthService.resetPassword(email);

      if (resetError) {
        // Don't reveal if email exists for security
        // Always show success message
        console.error('Password reset error:', resetError);
      }

      // Always show success message (security best practice)
      setIsSubmitted(true);
      toast.success('If an account with that email exists, a password reset link has been sent.');
    } catch (err) {
      console.error('Error requesting password reset:', err);
      // Still show success for security
      setIsSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="xs" sx={{ px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
        <Box sx={{ maxWidth: 500, mx: 'auto' }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              width: '100%',
              maxWidth: 400,
              mx: 'auto',
              borderRadius: 2,
              animation: `${fadeIn} 0.5s ease-out`,
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
              },
              transition: 'box-shadow 0.3s ease-in-out',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, width: '100%' }}>
                <Button
                  component={Link}
                  to="/login"
                  startIcon={<ArrowBackIcon />}
                  sx={{ mr: 'auto' }}
                  size="small"
                >
                  Back to Login
                </Button>
              </Box>

              <EmailIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />

              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}>
                Forgot Password?
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, textAlign: 'center' }}
              >
                {isSubmitted
                  ? 'Check your email for a password reset link. If you don\'t see it, check your spam folder.'
                  : 'Enter your email address and we\'ll send you a link to reset your password.'}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              {!isSubmitted ? (
                <Box component="form" sx={{ width: '100%' }} onSubmit={handleSubmit}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!error}
                    helperText={error}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                    sx={{
                      mt: 3,
                      mb: 2,
                      py: 1.5,
                      minHeight: 48,
                    }}
                  >
                    {isLoading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </Box>
              ) : (
                <Box sx={{ width: '100%', textAlign: 'center' }}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Password reset email sent! Please check your inbox.
                  </Alert>

                  <Button
                    component={Link}
                    to="/login"
                    variant="outlined"
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Back to Login
                  </Button>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Didn't receive the email? Check your spam folder or try again.
                  </Typography>

                  <Button
                    onClick={() => {
                      setIsSubmitted(false);
                      setEmail('');
                      setError('');
                    }}
                    variant="text"
                    size="small"
                  >
                    Try Again
                  </Button>
                </Box>
              )}

              <Box sx={{ mt: 3, textAlign: 'center', width: '100%' }}>
                <MuiLink
                  component={Link}
                  to="/login"
                  variant="body2"
                  color="info.main"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Remember your password? Sign in
                </MuiLink>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPassword;

